import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Link2, ExternalLink } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { resolveMediaUrl } from "@/lib/ddfApi";
import { isHttpsUrl } from "@/lib/url";
import { toast } from "sonner";
import { adProductIds } from "@/lib/adLinks";

function AdThumb({ ad }) {
  return ad.type === "video" ? (
    <video src={resolveMediaUrl(ad.url)} className="h-12 w-20 object-cover rounded" muted />
  ) : (
    <img src={resolveMediaUrl(ad.url)} alt="" className="h-12 w-20 object-cover rounded" />
  );
}

function ExternalUrlInput({ value, onCommit, testId }) {
  return (
    <Input
      type="url"
      defaultValue={value || ""}
      placeholder="https://… (optional external link, opens in new tab)"
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v === (value || "")) return;
        if (v && !isHttpsUrl(v)) {
          toast.error("External URL must be a valid link starting with https://");
          e.target.value = value || "";
          return;
        }
        onCommit(v);
      }}
      className="h-8 text-xs"
      data-testid={testId}
    />
  );
}

function ProductTickList({ value = [], options, onChange, testId }) {
  const selected = new Set(value);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(options.filter((o) => next.has(o.id)).map((o) => o.id));
  };
  return (
    <div className="rounded border bg-white" data-testid={testId}>
      <div className="flex items-center justify-between px-2 py-1 border-b text-[11px] text-muted-foreground">
        <span data-testid={`${testId}-count`}>{selected.size} selected</span>
        {selected.size > 0 && (
          <button type="button" className="underline" onClick={() => onChange([])} data-testid={`${testId}-clear`}>
            Clear
          </button>
        )}
      </div>
      <div className="max-h-44 overflow-y-auto divide-y">
        {options.length === 0 && <p className="p-2 text-xs italic text-muted-foreground">No promotion products yet.</p>}
        {options.map((o) => (
          <label
            key={o.id}
            className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50"
            data-testid={`${testId}-item-${o.id}`}
          >
            <Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} />
            {o.image ? (
              <img src={resolveMediaUrl(o.image)} alt="" className="h-7 w-7 rounded object-cover border" loading="lazy" />
            ) : (
              <span className="h-7 w-7 rounded border bg-slate-100" />
            )}
            <span className="flex-1 truncate">
              {o.name}
              {o.category && <span className="text-muted-foreground"> ({o.category})</span>}
            </span>
            {o.expired && (
              <span className="rounded px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700">Expired</span>
            )}
            {o.status === "holding" && (
              <span className="rounded px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600">Holding</span>
            )}
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                o.location === "Departures" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"
              }`}
            >
              {o.location}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NameInput({ value, onCommit, testId }) {
  return (
    <Input
      defaultValue={value || ""}
      placeholder="Banner name (optional, panel title)"
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (v !== (value || "")) onCommit(v);
      }}
      className="h-8 text-xs"
      data-testid={testId}
    />
  );
}

function AdLinkFields({ ad, idx, keyOf, prefix, options, onUpdateAd }) {
  const k = keyOf(ad, idx);
  return (
    <>
      <NameInput
        key={`${prefix}-name-${k}-${ad.name || ""}`}
        value={ad.name}
        onCommit={(v) => onUpdateAd(k, { name: v }, true)}
        testId={`admin-ad-${prefix}-name-${idx}`}
      />
      <ProductTickList
        value={adProductIds(ad)}
        options={options}
        onChange={(ids) => onUpdateAd(k, { linkPromoIds: ids, linkPromoId: "" }, true)}
        testId={`admin-ad-${prefix}-link-${idx}`}
      />
      <ExternalUrlInput
        key={`${prefix}-url-${k}-${ad.linkUrl || ""}`}
        value={ad.linkUrl}
        onCommit={(v) => onUpdateAd(k, { linkUrl: v }, true)}
        testId={`admin-ad-${prefix}-url-${idx}`}
      />
    </>
  );
}

export default function AdsTab(props) {
  const {
    draft, saving, adFileRef, adForm, setAdForm, promoOptions,
    adListTab, setAdListTab, onAddAd, onDeleteAd, onUpdateAd,
    onPublishAdNow, onMoveAdToHolding,
  } = props;

  const ads = draft.bannerAds || [];
  const keyOf = (ad, idx) => ad.id || ad.url || `ad-${idx}`;
  const liveAds = ads.filter((a) => a.status !== "holding");
  const holdingAds = ads.filter((a) => a.status === "holding");

  return (
    <div className="space-y-3 pt-4">
      <Label>Add Banner Ad (image / video)</Label>
      <Input
        type="file"
        accept="image/*,video/*"
        multiple
        ref={adFileRef}
        data-testid={ADMIN.adUpload}
      />
      <div className="grid gap-2 rounded-md border p-2">
        <Label className="text-xs">Banner name (optional)</Label>
        <Input
          value={adForm.name || ""}
          onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
          placeholder="e.g. Summer Fragrance Deals (shown as the product panel title)"
          className="h-8 text-xs"
          data-testid="admin-ad-name"
        />
        <Label className="text-xs flex items-center gap-1"><Link2 size={12} /> Link to promo product (optional, tick one or more)</Label>
        <ProductTickList
          value={adForm.linkPromoIds || []}
          options={promoOptions}
          onChange={(ids) => setAdForm({ ...adForm, linkPromoIds: ids })}
          testId="admin-ad-link-select"
        />
        <Label className="text-xs flex items-center gap-1"><ExternalLink size={12} /> Link banner to external URL (optional)</Label>
        <Input
          type="url"
          value={adForm.linkUrl || ""}
          onChange={(e) => setAdForm({ ...adForm, linkUrl: e.target.value })}
          placeholder="https://…"
          className="h-8 text-xs"
          data-testid="admin-ad-external-url"
        />
        <p className="text-[11px] text-muted-foreground">Must start with https://. Opens in a new tab. Used only when no ticked products are live.</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px]">Publish at (optional)</Label>
            <Input
              type="datetime-local"
              value={adForm.publishAt}
              onChange={(e) => setAdForm({ ...adForm, publishAt: e.target.value })}
              className="h-8 text-xs"
              data-testid="admin-ad-publishat"
            />
          </div>
          <div>
            <Label className="text-[11px]">Auto-remove at (optional)</Label>
            <Input
              type="datetime-local"
              value={adForm.removeAt}
              onChange={(e) => setAdForm({ ...adForm, removeAt: e.target.value })}
              className="h-8 text-xs"
              data-testid="admin-ad-removeat"
            />
          </div>
        </div>
      </div>
      <Button onClick={onAddAd} disabled={saving} data-testid={ADMIN.addAdBtn}>
        <Plus size={14} /> Add to Holding
      </Button>

      <Tabs value={adListTab} onValueChange={setAdListTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="live" data-testid="admin-ad-tab-live">Live ({liveAds.length})</TabsTrigger>
          <TabsTrigger value="holding" data-testid="admin-ad-tab-holding">Holding ({holdingAds.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="space-y-2 pt-3" data-testid="admin-ad-list-live">
          {liveAds.length === 0 && <p className="text-xs text-muted-foreground italic">No live ads.</p>}
          {ads.map((ad, idx) =>
            ad.status === "holding" ? null : (
              <div key={keyOf(ad, idx)} className="border rounded p-2 space-y-2" data-testid="admin-ad-live-row">
                <div className="flex items-center gap-2">
                  <AdThumb ad={ad} />
                  <span className="text-xs flex-1 truncate">{ad.name || ad.url}</span>
                  <Button variant="outline" size="sm" onClick={() => onMoveAdToHolding(keyOf(ad, idx))} disabled={saving}>
                    To Holding
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => onDeleteAd(keyOf(ad, idx))}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <AdLinkFields
                  ad={ad} idx={idx} keyOf={keyOf} prefix="live"
                  options={promoOptions} onUpdateAd={onUpdateAd}
                />
                <div>
                  <Label className="text-[11px]">Auto-remove at</Label>
                  <Input
                    type="datetime-local"
                    value={(ad.removeAt || "").slice(0, 16)}
                    onChange={(e) => onUpdateAd(keyOf(ad, idx), { removeAt: e.target.value })}
                    onBlur={() => onUpdateAd(keyOf(ad, idx), {}, true)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="holding" className="space-y-2 pt-3" data-testid="admin-ad-list-holding">
          <p className="text-xs text-muted-foreground">
            Ads wait here until you publish them or their scheduled publish time passes.
            They auto-remove once the auto-remove time passes.
          </p>
          {holdingAds.length === 0 && <p className="text-xs text-muted-foreground italic">Holding is empty.</p>}
          {ads.map((ad, idx) =>
            ad.status !== "holding" ? null : (
              <div key={keyOf(ad, idx)} className="border rounded p-2 space-y-2" data-testid="admin-ad-holding-row">
                <div className="flex items-center gap-2">
                  <AdThumb ad={ad} />
                  <span className="text-xs flex-1 truncate">{ad.name || ad.url}</span>
                  <Button size="sm" onClick={() => onPublishAdNow(keyOf(ad, idx))} disabled={saving} data-testid={`admin-ad-publish-${idx}`}>
                    Publish Now
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => onDeleteAd(keyOf(ad, idx))}>
                    <Trash2 size={14} />
                  </Button>
                </div>
                <AdLinkFields
                  ad={ad} idx={idx} keyOf={keyOf} prefix="holding"
                  options={promoOptions} onUpdateAd={onUpdateAd}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px]">Publish at</Label>
                    <Input
                      type="datetime-local"
                      value={(ad.publishAt || "").slice(0, 16)}
                      onChange={(e) => onUpdateAd(keyOf(ad, idx), { publishAt: e.target.value })}
                      onBlur={() => onUpdateAd(keyOf(ad, idx), {}, true)}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px]">Auto-remove at</Label>
                    <Input
                      type="datetime-local"
                      value={(ad.removeAt || "").slice(0, 16)}
                      onChange={(e) => onUpdateAd(keyOf(ad, idx), { removeAt: e.target.value })}
                      onBlur={() => onUpdateAd(keyOf(ad, idx), {}, true)}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
                {ad.publishAt && (
                  <p className="text-[11px] text-muted-foreground">Scheduled: {ad.publishAt.replace("T", " ")}</p>
                )}
              </div>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
